import React, { useRef, useState, useEffect } from "react";
import "./invoice.css";
import { useLocation, useNavigate } from "react-router-dom";
import { getProfile } from "../../Apiservices/Auth/servers/profileServer";
import { useSelector } from "react-redux";
import config from "../../config";
import { getRegionContactInfo } from "../../util/contactRegion";
import thurentLogo from "../../redesign/assets/thurent-logo.svg";

const InvoicePage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const invoice = state?.invoice;
  const activeContactInfo = getRegionContactInfo();
  const company = (state?.company && Object.keys(state.company).length > 0) ? state.company : {
    name: activeContactInfo?.companyName || config.companyFullName,
    gstin: config.isIndia ? "36AALCT2102L1ZB" : "",
    cin: config.isIndia ? "U62090TS2024PTC188514" : "",
    address: config.isIndia
      ? "T-Hub Phase II Gachibowli, Hyderabad, Telangana – 500060"
      : activeContactInfo?.address || "Manfield, TX",
    mobile: activeContactInfo?.phone || "",
    email: activeContactInfo?.email || "",
    website: config.isIndia ? "https://thurent.in" : "https://thurent.us",
  };

  const reduxUser = useSelector((state) => state.user?.userDetails);
  const [profile, setProfile] = useState(state?.user || reduxUser || {});
  const [shareSuccess, setShareSuccess] = useState(false);
  const invoiceRef = useRef();

  useEffect(() => {
    if (!profile.firstName && !profile.mobileNumber) {
      getProfile()
        .then((data) => { if (data) setProfile(data); })
        .catch((err) => console.error("Failed to load profile:", err));
    }
  }, [profile.firstName, profile.mobileNumber]);

  // ─── Format helpers ──────────────────────────────────────────────
  const formatDate = (date) => {
    if (!date) return "–";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "–";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  };

  const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

  // ─── Share / PDF ──────────────────────────────────────────────────
  const handleShare = async () => {
    const title = `${invoice?.type?.toUpperCase() || "ESTIMATE"} – ${invoice?.invoiceNumber}`;
    const text = `Thurent ${invoice?.type || "Estimate"} for ${invoice?.eventTitle || "your event"}\nTotal: ${formatCurrency(invoice?.grandTotal)}`;

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url: window.location.href });
        return;
      } catch (_) { /* user cancelled or API unavailable */ }
    }
    // Fallback: copy invoice number to clipboard
    try {
      await navigator.clipboard.writeText(`${title}\n${text}`);
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 2500);
    } catch (_) { }
  };

  const handleDownloadPDF = () => {
    const originalTitle = document.title;
    document.title = `${invoice?.type?.toUpperCase() || "ESTIMATE"}_${invoice?.invoiceNumber}`;
    window.print();
    document.title = originalTitle;
  };

  // ─── Guard states ─────────────────────────────────────────────────
  if (!invoice) {
    return (
      <div className="inv-page inv-empty-state">
        <div className="inv-empty-icon">📄</div>
        <h2>No Estimate Data</h2>
        <p>This estimate is unavailable. Please go back to your events and try again.</p>
        <button className="inv-back-btn" onClick={() => navigate("/dashboard")}>
          ← Back to Events
        </button>
      </div>
    );
  }

  const isEstimate = invoice.type === "estimate";
  const hasItems = invoice.items?.length > 0;

  return (
    <div className="inv-page" id="invoice-page">
      {/* ── Top action bar (hidden when printing) ── */}
      <div className="inv-action-bar no-print">
        <button className="inv-back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div className="inv-action-right">
          <button className="inv-share-btn" onClick={handleShare}>
            {shareSuccess ? "✓ Copied!" : "⬆ Share"}
          </button>
          <button className="inv-pdf-btn" onClick={handleDownloadPDF}>
            ↓ Download PDF
          </button>
        </div>
      </div>

      {/* ── Printable invoice wrapper ── */}
      <div className="inv-wrapper" ref={invoiceRef}>

        {/* Header: company info + logo */}
        <div className="inv-header-card">
          <div className="inv-company-block">
            <img src={thurentLogo} alt="Thurent" className="inv-logo" onError={(e) => { e.target.style.display = 'none'; }} />
            <div className="inv-company-info">
              <div className="inv-company-name">{company.name}</div>
              <div className="inv-company-detail">{company.address}</div>
              {company.gstin && (
                <div className="inv-company-detail">
                  <strong>GSTIN:</strong> {company.gstin}
                  {company.cin && <span> | <strong>CIN:</strong> {company.cin}</span>}
                </div>
              )}
              <div className="inv-company-detail" style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap", marginTop: "4px" }}>
                {company.mobile && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#26B8E3"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                    {company.mobile}
                  </span>
                )}
                {company.email && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#26B8E3"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    {company.email}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Estimate title + invoice number */}
          <div className="inv-title-block">
            <div className="inv-type-label">{invoice.type?.toUpperCase() || "ESTIMATE"}</div>
            <div className="inv-number">{invoice.invoiceNumber}</div>
            <div className="inv-date">Date: {formatDateTime(invoice.generatedDate)}</div>
          </div>
        </div>

        {/* Bill To + Event Details */}
        <div className="inv-parties-row">
          <div className="inv-party-card">
            <div className="inv-section-title">Bill To</div>
            <div className="inv-party-name">
              {profile.firstName
                ? `${profile.firstName} ${profile.lastName || ""}`.trim()
                : "–"}
            </div>
            {profile.mobileNumber && (
              <div className="inv-party-detail">{profile.mobileNumber}</div>
            )}
            {profile.email && (
              <div className="inv-party-detail">{profile.email}</div>
            )}
          </div>

          <div className="inv-party-card">
            <div className="inv-section-title">Event Details</div>
            <div className="inv-party-name">
              {invoice.eventTitle || "–"}
            </div>
            {invoice.eventDate && (
              <div className="inv-party-detail">
                <strong>Start:</strong> {formatDate(invoice.eventDate)}
              </div>
            )}
            {invoice.eventEndDate && (
              <div className="inv-party-detail">
                <strong>End:</strong> {formatDate(invoice.eventEndDate)}
              </div>
            )}
            {invoice.numEventDays > 1 && (
              <div className="inv-party-detail">
                <strong>Duration:</strong> {invoice.numEventDays} days
              </div>
            )}
            {invoice.venue && (
              <div className="inv-party-detail">{invoice.venue}</div>
            )}
          </div>
        </div>

        {/* Service Details table */}
        <div className="inv-services-card">
          <div className="inv-section-title">Service Details</div>

          {hasItems ? (
            <table className="inv-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Provider</th>
                  <th className="inv-th-amount">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, i) => (
                  <tr key={i}>
                    <td>
                      <div>{item.description || "–"}</div>
                      {item.details?.numDays > 1 && (
                        <div className="inv-sub-detail">
                          {item.details.fromDate && item.details.toDate
                            ? `${formatDate(item.details.fromDate)} – ${formatDate(item.details.toDate)}`
                            : `${item.details.numDays} days`}
                          {item.details.perDayAmount
                            ? ` (₹${Number(item.details.perDayAmount).toLocaleString("en-IN")}/day × ${item.details.numDays} days)`
                            : ` (${item.details.numDays} days)`}
                        </div>
                      )}
                    </td>
                    <td>{item.providerName || "–"}</td>
                    <td className="inv-td-amount">{formatCurrency(item.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="inv-no-items">
              No services added yet. Add services from My Events to see them here.
            </div>
          )}
        </div>

        {/* Totals */}
        <div className="inv-totals-card">
          <div className="inv-total-row">
            <span>Subtotal</span>
            <span>{formatCurrency(invoice.subtotal)}</span>
          </div>
          <div className="inv-total-row">
            <span>Tax (GST)</span>
            <span>{formatCurrency(invoice.totalTax)}</span>
          </div>
          <div className="inv-divider" />
          <div className="inv-total-row inv-grand-total">
            <span>Grand Total</span>
            <span>{formatCurrency(invoice.grandTotal)}</span>
          </div>

          {invoice.paidAmount > 0 && (
            <>
              <div className="inv-total-row inv-paid">
                <span>Paid</span>
                <span>–{formatCurrency(invoice.paidAmount)}</span>
              </div>
              <div className="inv-total-row inv-balance">
                <span>Balance Due</span>
                <span>{formatCurrency(invoice.pendingAmount)}</span>
              </div>
            </>
          )}
        </div>

        {/* Terms & Conditions (estimate only) */}
        {isEstimate && (
          <div className="inv-terms-card">
            <div className="inv-section-title">Terms &amp; Conditions</div>
            <ul className="inv-terms-list">
              <li>Payment to be made in advance.</li>
              <li>Any additional items or services not mentioned above will be charged extra.</li>
              <li>Permission charges are subject to actuals.</li>
              <li>This estimate is valid for 15 days from the date of issue.</li>
            </ul>
            <div className="inv-signature-row">
              <div className="inv-signature-block">
                <div className="inv-signature-line" />
                <div className="inv-signature-label">Authorised Signatory</div>
                <div className="inv-signature-company">For {company.name}</div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="inv-footer no-print">
          <span>{company.website}</span>
        </div>
      </div>
    </div>
  );
};

export default InvoicePage;